import { Box, Card, FormErrorMessage, NumberInput, Radio, RadioGroup, Text, TextArea } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useState } from 'react';
import type { IProcessAction } from '../../../../../../common/interfaces/IProcessAction';
import type {
    IOsasLifeStyleFormCustomControlData,
    IOsasLifeStyleFormCustomControlDataWithPage,
} from '../../../../../../common/validation/customControls/osasFlex';
import type { ValidationErrors } from '../../../../../utils/validation';
import { ProcessActivityStatus } from '../../../../../../common/enums/ProcessActivityStatus';
import { YesOrNoOrStoppedOrUnknown } from '../../../../../../common/enums/YesOrNoOrStopped';
import {
    lifestyleQuestionnaireDraftSchema,
    lifestyleQuestionnaireFinalSchema,
} from '../../../../../../common/validation/customControls/osasFlex';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { useReloadPatientAvailableActions } from '../../../../../utils/hooks/useReloadPatientAvailableActions';
import { emptyValidationErrors, validate } from '../../../../../utils/validation';
import { CustomContainer } from '../../../../elements/molecules/customContainer/CustomContainer';
import { Stack } from '../../../../elements/molecules/stack/Stack';
import { PageIntro } from '../../../../elements/organisms/pageIntro/PageIntro';
import { StyledFormGroup } from '../../../../elements/organisms/StyledFormGroup/StyledFormGroup';
import { QuestionnaireFormButtons } from '../../../questionnaireForm/QuestionnaireFormButtons';
import { questions } from './questions';

interface ILifestyleQuestionnaireFormProps {
    action: IProcessAction;
    activityData: IOsasLifeStyleFormCustomControlDataWithPage;
    onSave: () => void;
    serviceActivityId?: string;
}

export function LifestyleQuestionnaireForm(props: Readonly<ILifestyleQuestionnaireFormProps>) {
    const { action, onSave, serviceActivityId } = props;
    const reloadPatientAvailableActions = useReloadPatientAvailableActions();
    const navigate = useNavigate();

    const [currentPage, setCurrentPage] = useState(props.activityData.page === 0 ? 1 : props.activityData.page);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [activityData, setActivityData] = useState(props.activityData);

    const processId = action.processId;

    const questionsOnThisPage = questions.filter((question) => question.page === currentPage);

    return (
        <>
            <PageIntro title={`Een paar extra vragen deel ${currentPage}/4`} />

            <CustomContainer>
                {questionsOnThisPage.filter(shouldShowBasedOnPreviousAnswers).map((question) => {
                    const hasError = errors.has(question.fieldkey);

                    return (
                        <Stack
                            className={activityData[question.fieldkey] ? '' : `empty ${question.fieldkey}`}
                            key={question.fieldkey}
                        >
                            <StyledFormGroup
                                hasErrors={hasError}
                                isRequired={question.isRequired}
                                label={question.label}
                                layout="vertical"
                            >
                                <Card style={{ marginBottom: 0 }}>
                                    <Card.Body>
                                        {question.type === 'number' && (
                                            <Box
                                                backgroundColor="white"
                                                display="grid"
                                                gridTemplateColumns="80px max-content"
                                            >
                                                <NumberInput
                                                    aria-label={question.label}
                                                    id={question.fieldkey}
                                                    maximumFractionDigits={2}
                                                    name={question.fieldkey}
                                                    onChange={(value) => onChange({ [question.fieldkey]: value })}
                                                    size="medium"
                                                    value={(activityData[question.fieldkey] as number) ?? null}
                                                />

                                                <Text
                                                    fontWeight="medium"
                                                    ml={3}
                                                    mt={2}
                                                    size="sm"
                                                >
                                                    {question.suffix}
                                                </Text>
                                            </Box>
                                        )}
                                        {question.type === 'text' && (
                                            <TextArea
                                                aria-label={question.label}
                                                id={question.fieldkey}
                                                name={question.fieldkey}
                                                onChange={async (event) => {
                                                    const value = event.target.value === '' ? null : event.target.value;
                                                    onChange({ [question.fieldkey]: value ?? null });
                                                }}
                                                size="medium"
                                                value={(activityData[question.fieldkey] as string) ?? ''}
                                            />
                                        )}

                                        {question.type === 'radio' && (
                                            <RadioGroup
                                                className={question.fieldkey}
                                                layout="row"
                                                onChange={(e) => onChange({ [question.fieldkey]: e.target.value })}
                                                value={
                                                    (activityData[question.fieldkey] as YesOrNoOrStoppedOrUnknown) ??
                                                    null
                                                }
                                            >
                                                <Radio
                                                    key={YesOrNoOrStoppedOrUnknown.yes}
                                                    label={YesOrNoOrStoppedOrUnknown.yes}
                                                    value={YesOrNoOrStoppedOrUnknown.yes}
                                                />
                                                <Radio
                                                    key={YesOrNoOrStoppedOrUnknown.no}
                                                    label={YesOrNoOrStoppedOrUnknown.no}
                                                    value={YesOrNoOrStoppedOrUnknown.no}
                                                />
                                                <Radio
                                                    key={YesOrNoOrStoppedOrUnknown.stopped}
                                                    label={YesOrNoOrStoppedOrUnknown.stopped}
                                                    value={YesOrNoOrStoppedOrUnknown.stopped}
                                                />
                                                <Radio
                                                    key={YesOrNoOrStoppedOrUnknown.unknown}
                                                    label="Zeg ik liever niet"
                                                    value={YesOrNoOrStoppedOrUnknown.unknown}
                                                />
                                            </RadioGroup>
                                        )}
                                    </Card.Body>
                                </Card>

                                {hasError && (
                                    <FormErrorMessage>
                                        {translateKey('validation.' + errors.get(question.fieldkey))}
                                    </FormErrorMessage>
                                )}
                            </StyledFormGroup>
                        </Stack>
                    );
                })}

                <QuestionnaireFormButtons
                    currentPage={currentPage}
                    goToNextPage={goToNextPage}
                    goToPreviousPage={goToPreviousPage}
                    nrOfPages={4}
                    sendQuestionnaire={sendQuestionnaire}
                />
            </CustomContainer>
        </>
    );

    function goToNextPage() {
        const hasErrors = validateForm(ProcessActivityStatus.draft, activityData);

        if (!hasErrors) {
            setCurrentPage(currentPage + 1);

            const updatedActivityData = { ...activityData, page: currentPage };
            setActivityData(updatedActivityData);

            save(ProcessActivityStatus.draft, updatedActivityData);
        }
    }

    function goToPreviousPage() {
        setCurrentPage(currentPage - 1);
    }

    function sendQuestionnaire() {
        save(ProcessActivityStatus.complete, activityData);
    }

    function onChange(update: Partial<IOsasLifeStyleFormCustomControlData>) {
        let dependentQuestionsObject: Record<string, null> = {};

        for (const key in update) {
            const dependentQuestions = questions.filter(
                (q) => q.dependentOn?.[key as keyof IOsasLifeStyleFormCustomControlData]
            );

            dependentQuestionsObject = dependentQuestions.reduce((acc, question) => {
                acc[question.fieldkey] = null;

                return acc;
            }, dependentQuestionsObject);
        }

        const updatedActivityData = { ...activityData, ...update, ...dependentQuestionsObject, page: currentPage };

        setActivityData(updatedActivityData);

        save(ProcessActivityStatus.draft, updatedActivityData);
    }

    async function save(mode: ProcessActivityStatus, data: IOsasLifeStyleFormCustomControlDataWithPage) {
        const hasErrors = validateForm(mode, data);

        if (hasErrors) {
            return false;
        }

        if (!serviceActivityId) {
            return false;
        }

        const payload = {
            actionId: action.id,
            activityData: {
                [serviceActivityId]: data,
            },
            processWorkflowStepId: action.processWorkflowStepId,
            serviceWorkflowStepId: action.serviceWorkflowStep.id,
            status: mode,
        };

        const result = await fetchJson(`/api/patient/processes/${processId}`, 'POST', payload);

        if (result.status === 200) {
            onSave();

            if (!action.processWorkflowStepId || mode === ProcessActivityStatus.complete) {
                await reloadPatientAvailableActions();

                if (mode === ProcessActivityStatus.complete) {
                    navigate(`/osas/questionnaires/${encodeURIComponent(action.label)}/success`);
                }
            }
        }
    }

    function validateForm(mode: ProcessActivityStatus, data: IOsasLifeStyleFormCustomControlDataWithPage) {
        let validationSchema = lifestyleQuestionnaireDraftSchema;

        if (mode === ProcessActivityStatus.complete) {
            validationSchema = lifestyleQuestionnaireFinalSchema;
        }

        const validationResult = validate({ schema: validationSchema }, data);

        setErrors(validationResult.errors);

        return validationResult.hasErrors;
    }

    function shouldShowBasedOnPreviousAnswers(question: {
        dependentOn?: Partial<IOsasLifeStyleFormCustomControlDataWithPage>;
    }) {
        if (!question.dependentOn) {
            return true;
        }
        const answerValue =
            activityData[
                Object.entries(question.dependentOn)[0][0] as keyof IOsasLifeStyleFormCustomControlDataWithPage
            ];
        const questionDependentOnValue = Object.entries(question.dependentOn)[0][1];

        return answerValue === questionDependentOnValue;
    }
}
